import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Truck, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

function formatApiError(detail) {
    if (detail == null) return "Qualcosa è andato storto. Riprova.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    if (detail?.msg) return detail.msg;
    return String(detail);
}

export default function LoginPage() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login, demoLogin } = useAuth();
    const navigate = useNavigate();
    const [demoLoading, setDemoLoading] = useState('');

    const handleDemo = async (role) => {
        setDemoLoading(role);
        try {
            const user = await demoLogin(role);
            toast.success('Accesso demo effettuato!');
            navigate(user.role === 'admin' ? '/admin' : '/client');
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setDemoLoading('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(username, password, rememberMe);
            toast.success('Accesso effettuato!');
            if (user.must_change_password) {
                navigate('/change-password');
            } else {
                navigate(user.role === 'admin' ? '/admin' : '/client');
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-split">
            <div className="hidden md:flex flex-col items-center justify-center p-12 bg-gradient-to-br from-[#EAF2FB] via-[#F4F8FD] to-[#DCEAF8]">
                <img src="/omniglide-logo.png" alt="OmniGlide" className="w-full max-w-[420px] object-contain" />
            </div>
            
            <div className="flex items-center justify-center p-8 bg-[#F2F5F2]">
                <Card className="w-full max-w-md border border-[#C5CFC5] shadow-none">
                    <CardHeader className="space-y-1">
                        <div className="flex items-center gap-3 md:hidden mb-4">
                            <img src="/omniglide-icon.png" alt="OmniGlide" className="h-11 w-11 rounded-xl object-contain border border-[#E2E8F0]" />
                            <span className="text-xl font-bold text-[#1B4E8E]" style={{ fontFamily: 'Work Sans, sans-serif' }}>OmniGlide</span>
                        </div>
                        <CardTitle className="text-2xl text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>Accedi</CardTitle>
                        <CardDescription className="text-[#64748B]">Inserisci le tue credenziali per accedere</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="username" className="text-[#0F172A]">Email o nome utente</Label>
                                <Input
                                    id="username"
                                    data-testid="login-username-input"
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Inserisci email o nome utente"
                                    required
                                    className="border-[#CBD5E1] focus:ring-[#1B4E8E]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-[#0F172A]">Password</Label>
                                <Input
                                    id="password"
                                    data-testid="login-password-input"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Inserisci password"
                                    required
                                    className="border-[#CBD5E1] focus:ring-[#1B4E8E]"
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <label htmlFor="remember-me" className="flex items-center gap-2 cursor-pointer select-none">
                                    <Checkbox
                                        id="remember-me"
                                        checked={rememberMe}
                                        onCheckedChange={(v) => setRememberMe(v === true)}
                                        data-testid="login-remember-me"
                                        className="data-[state=checked]:bg-[#1B4E8E] data-[state=checked]:border-[#1B4E8E]"
                                    />
                                    <span className="text-sm text-[#0F172A]">Ricordami</span>
                                </label>
                                <span className="text-xs text-[#64748B]">{rememberMe ? '30 giorni' : '8 ore'}</span>
                            </div>
                            <Button 
                                type="submit" 
                                data-testid="login-submit-btn"
                                className="w-full bg-[#1B4E8E] hover:bg-[#143A6B] text-white"
                                disabled={loading}
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Accedi
                            </Button>
                        </form>

                        <div className="mt-5 pt-5 border-t border-dashed border-[#CBD5E1]" data-testid="demo-section">
                            <p className="text-center text-sm font-medium text-[#0F172A]">Prova la demo</p>
                            <p className="text-center text-xs text-[#64748B] mt-1 mb-3">Esplora l'app con dati di esempio, senza registrarti.</p>
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="border-brand text-brand hover:bg-[color:var(--brand-color)] hover:text-white"
                                    onClick={() => handleDemo('admin')}
                                    disabled={!!demoLoading}
                                    data-testid="demo-admin-btn"
                                >
                                    {demoLoading === 'admin' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Demo Admin
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="border-[#1B4E8E] text-[#1B4E8E] hover:bg-[#1B4E8E]/5"
                                    onClick={() => handleDemo('client')}
                                    disabled={!!demoLoading}
                                    data-testid="demo-client-btn"
                                >
                                    {demoLoading === 'client' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Demo Cliente
                                </Button>
                            </div>
                        </div>

                        <div className="mt-4 text-center">
                            <Link 
                                to="/forgot-password" 
                                data-testid="forgot-password-link"
                                className="text-sm text-[#1B4E8E] hover:underline"
                            >
                                Password dimenticata?
                            </Link>
                        </div>
                        <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-center">
                            <span className="text-sm text-[#64748B]">Non hai un account? </span>
                            <Link to="/signup" data-testid="login-signup-link" className="text-sm text-[#1B4E8E] font-medium hover:underline">
                                Registra la tua azienda
                            </Link>
                        </div>

                        <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-center" data-testid="login-support-contact">
                            <p className="text-xs text-[#64748B]">Assistenza o informazioni</p>
                            <a
                                href="mailto:infomniglide@gmail.com"
                                data-testid="login-support-email"
                                className="text-sm text-[#1B4E8E] font-medium hover:underline"
                            >
                                infomniglide@gmail.com
                            </a>
                        </div>

                        <p className="mt-4 text-[10px] leading-relaxed text-[#94A3B8] text-center" data-testid="login-trademark-disclaimer">
                            Questo software è un'integrazione indipendente e non è in alcun modo affiliato, sponsorizzato,
                            autorizzato o approvato da TeamSystem S.p.A. Tutti i marchi registrati (inclusi Danea, Easyfatt
                            e Fatture in Cloud) appartengono ai rispettivi proprietari.
                        </p>
                        <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] text-[#94A3B8]" data-testid="login-legal-links">
                            <Link to="/terms" className="hover:text-[#1B4E8E] hover:underline">Termini</Link>
                            <Link to="/privacy" className="hover:text-[#1B4E8E] hover:underline">Privacy</Link>
                            <Link to="/dpa" className="hover:text-[#1B4E8E] hover:underline">DPA</Link>
                            <Link to="/sub-responsabili" className="hover:text-[#1B4E8E] hover:underline">Sub-responsabili</Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
